import { useQuery } from "@tanstack/react-query";
import { CircleCheck, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { formatInt, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { SendingCard, WarmupControls } from "./SendingControls";

interface Stage {
  stage: number;
  engaged_days: number;
  daily_cap: number;
  min_days: number;
  note: string;
}

export interface WarmupStatus {
  active: boolean;
  stage: number;
  current: Stage | null;
  next: Stage | null;
  sent_today: number;
  cap_today: number;
  remaining: number;
  day_started: string;
  plan: Stage[];
  auto_advance: boolean;
  last_7_days_sent: number;
  last_7_days_delivered: number;
  last_7_days_hard_bounces: number;
  last_7_days_complaints: number;
  bounce_rate: number;
  complaint_rate: number;
  healthy: boolean;
  days_at_stage: number;
  advice: string;
}

// The limits behind `healthy` (internal/email/warmup/warmup.go): bounces per sent, complaints per delivered.
const MAX_BOUNCE = 0.02;
const MAX_COMPLAINT = 0.0008;

const pct = (v: number) => (v === 0 ? "0%" : v < 0.001 ? `${(v * 100).toFixed(3)}%` : v < 0.1 ? `${(v * 100).toFixed(2)}%` : formatPercent(v));
const who = (s: Stage) => (s.engaged_days <= 0 ? "Everyone who can be mailed" : s.engaged_days >= 730 ? "Engaged in the last 2 years" : s.engaged_days >= 365 ? "Engaged in the last year" : `Engaged in the last ${s.engaged_days} days`);

export function useWarmup() {
  return useQuery({ queryKey: ["email-warmup"], queryFn: () => api<WarmupStatus>("/api/v1/email/warmup"), refetchInterval: 60_000, retry: false });
}

/** Email → Warm-up: where the dedicated sending IP's warm-up stands, the pause switch and stage changes (each asks to confirm first). */
export function WarmupPage() {
  const q = useWarmup();
  const w = q.data;
  return (
    <div className="mx-auto max-w-[1100px] space-y-3">
      <PageHeader title="IP warm-up" sub="Broadcasts from the new sending address">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Mailbox providers trust a new sending address slowly, so broadcasts go first to the people most likely to open them, and only so many a day. Each stage widens the group and
          raises the daily limit while bounces and spam complaints stay low. Receipts, sign-in links and other one-to-one emails aren't counted. Pausing sending or
          changing the stage tells you what will happen and asks you to confirm first.
        </p>
      </PageHeader>
      {q.isLoading && <Skeleton className="h-40" />}
      {q.error && <Empty title="The warm-up status isn't available on this server.">{q.error.message}</Empty>}
      {w && !w.active && (
        <Card>
          <CardContent className="flex items-center gap-2 py-4 text-sm">
            <CircleCheck className="size-5 text-[var(--color-success-fg)]" /> The warm-up is finished: broadcasts go to everyone who can be mailed, with no daily limit.
          </CardContent>
        </Card>
      )}
      <SendingCard />
      {w && w.active && w.current && <Status w={w} />}
      {w && w.plan.length > 0 && <WarmupControls key={`${w.stage}-${w.auto_advance}`} w={w} />}
      {w && w.plan.length > 0 && <Plan w={w} />}
    </div>
  );
}

function Status({ w }: { w: WarmupStatus }) {
  const cur = w.current!;
  const used = w.cap_today > 0 ? Math.min(1, w.sent_today / w.cap_today) : 0;
  return (
    <>
      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Stage</CardTitle>
            <Badge variant={w.auto_advance ? "default" : "muted"} title={w.auto_advance ? "Moves to the next stage each morning while healthy" : "Moves only when someone changes it"}>
              {w.auto_advance ? "advances on its own" : "moved by hand"}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="text-3xl font-semibold">
              {cur.stage} <span className="text-base font-normal text-[var(--color-muted-foreground)]">of {w.plan.length}</span>
            </div>
            <div>{who(cur)}</div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              {w.days_at_stage} {w.days_at_stage === 1 ? "day" : "days"} at this stage; at least {cur.min_days} before the next.
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Today</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="num text-3xl font-semibold">
              {formatInt(w.sent_today)} <span className="text-base font-normal text-[var(--color-muted-foreground)]">of {formatInt(w.cap_today)} sent</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-[var(--color-muted)]"
              role="meter"
              aria-label="Sent today"
              aria-valuemin={0}
              aria-valuemax={w.cap_today}
              aria-valuenow={w.sent_today}
            >
              <div className="h-full rounded-full bg-[var(--color-primary)]" style={{ width: `${used * 100}%` }} />
            </div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              {formatInt(w.remaining)} left today. Broadcasts past the limit carry over to tomorrow (Mountain Time).
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Last 7 days</CardTitle>
            {w.healthy ? <Badge variant="success">healthy</Badge> : <Badge variant="destructive">too many problems</Badge>}
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Rate label="Bounced" value={w.bounce_rate} limit={MAX_BOUNCE} count={w.last_7_days_hard_bounces} of={`${formatInt(w.last_7_days_sent)} sent`} />
            <Rate label="Marked as spam" value={w.complaint_rate} limit={MAX_COMPLAINT} count={w.last_7_days_complaints} of={`${formatInt(w.last_7_days_delivered)} delivered`} />
            <p className="pt-1 text-xs text-[var(--color-muted-foreground)]">If bounces or complaints stay over the limit, you get one email that day. Sending is not stopped.</p>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardContent className={cn("flex items-start gap-2 py-3 text-sm", !w.healthy && "text-[var(--color-danger-fg)]")}>
          {w.healthy ? <CircleCheck className="mt-0.5 size-4 shrink-0 text-[var(--color-success-fg)]" /> : <TriangleAlert className="mt-0.5 size-4 shrink-0" />}
          <span>
            {w.advice}
            {w.next && (
              <span className="text-[var(--color-muted-foreground)]">
                {" "}
                Next: stage {w.next.stage}, {who(w.next).toLowerCase()}, up to {formatInt(w.next.daily_cap)} a day.
              </span>
            )}
          </span>
        </CardContent>
      </Card>
    </>
  );
}

function Rate({ label, value, limit, count, of }: { label: string; value: number; limit: number; count: number; of: string }) {
  const over = value > limit;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span>{label}</span>
        <span className={cn("num font-semibold", over && "text-[var(--color-danger-fg)]")}>{pct(value)}</span>
      </div>
      <div className="text-xs text-[var(--color-muted-foreground)]">
        {formatInt(count)} of {of}; limit {pct(limit)}
      </div>
    </div>
  );
}

function Plan({ w }: { w: WarmupStatus }) {
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>The plan</CardTitle>
      </CardHeader>
      <Table>
        <THead>
          <tr>
            <TH>Stage</TH>
            <TH>Who gets broadcasts</TH>
            <TH className="text-right">Per day, up to</TH>
            <TH className="text-right">At least</TH>
          </tr>
        </THead>
        <tbody>
          {w.plan.map((s) => {
            const now = w.active && s.stage === w.stage;
            const done = !w.active || s.stage < w.stage;
            return (
              <TR key={s.stage} className={cn(now && "bg-[var(--color-row-selected)]")} aria-current={now ? "step" : undefined}>
                <TD className="num whitespace-nowrap">
                  {s.stage}
                  {now && (
                    <Badge variant="default" className="ml-2">
                      now
                    </Badge>
                  )}
                  {done && !now && <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">done</span>}
                </TD>
                <TD>
                  {who(s)}
                  <div className="text-xs text-[var(--color-muted-foreground)]">{s.note}</div>
                </TD>
                <TD className="num text-right">{formatInt(s.daily_cap)}</TD>
                <TD className="num text-right whitespace-nowrap">
                  {s.min_days} {s.min_days === 1 ? "day" : "days"}
                </TD>
              </TR>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}
